package com.rosario.juridico.ui.screens

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.ArrowForward
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.NavController
import com.rosario.juridico.domain.model.PasoProcedimientoPromesa

// Paleta visual alineada con Agenda Expedientes
private val AzulPrimario = Color(0xFF1565C0)
private val VerdeOk = Color(0xFF2E7D32)
private val GrisBloqueado = Color(0xFFB0BEC5)
private val FondoAlerta = Color(0xFFFFF3E0)
private val TextoAlerta = Color(0xFFE65100)

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AgendaPromesaScreen(
    navController: NavController,
    viewModel: AgendaPromesaViewModel = androidx.lifecycle.viewmodel.compose.viewModel { AgendaPromesaViewModel() }
) {
    val pasos by viewModel.pasos.collectAsState()
    val activeIndex by viewModel.currentActiveStepIndex.collectAsState()
    val loading by viewModel.loading.collectAsState()

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("Agenda Asistida PROMESA", fontWeight = FontWeight.Bold, color = Color.White) },
                navigationIcon = {
                    IconButton(onClick = { navController.popBackStack() }) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Atrás", tint = Color.White)
                    }
                },
                actions = {
                    IconButton(onClick = { viewModel.resetProgress() }) {
                        Icon(Icons.Default.Refresh, contentDescription = "Reiniciar", tint = Color.White)
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(containerColor = AzulPrimario)
            )
        }
    ) { paddingValues ->
        if (loading) {
            Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AzulPrimario)
            }
        } else {
            LazyColumn(
                modifier = Modifier.fillMaxSize().padding(paddingValues),
                contentPadding = PaddingValues(16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                item {
                    Text(
                        "Procedimiento Decreto 379/2025",
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                        color = AzulPrimario
                    )
                    Text(
                        "Asistente paso a paso para la mediación en salud.",
                        style = MaterialTheme.typography.bodySmall,
                        color = Color.Gray
                    )
                    Spacer(Modifier.height(8.dp))
                }

                itemsIndexed(pasos) { index, paso ->
                    PasoCard(
                        paso = paso,
                        isCompleted = index < activeIndex,
                        isActive = index == activeIndex,
                        isLocked = index > activeIndex,
                        onComplete = { viewModel.markStepAsCompleted() }
                    )
                }
                
                item {
                    Spacer(Modifier.height(80.dp))
                }
            }
        }
    }
}

@Composable
fun PasoCard(
    paso: PasoProcedimientoPromesa,
    isCompleted: Boolean,
    isActive: Boolean,
    isLocked: Boolean,
    onComplete: () -> Unit
) {
    val alpha by animateFloatAsState(if (isLocked) 0.5f else 1f)
    
    Card(
        modifier = Modifier.fillMaxWidth().alpha(alpha),
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(
            containerColor = if (isActive) Color.White else MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f)
        ),
        elevation = CardDefaults.cardElevation(defaultElevation = if (isActive) 4.dp else 1.dp),
        border = if (isActive) BorderStroke(2.dp, AzulPrimario) else null
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                // Indicador de estado
                Surface(
                    modifier = Modifier.size(32.dp),
                    shape = CircleShape,
                    color = when {
                        isCompleted -> VerdeOk
                        isActive -> AzulPrimario
                        else -> GrisBloqueado
                    }
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        if (isCompleted) {
                            Icon(Icons.Default.Check, null, tint = Color.White, modifier = Modifier.size(18.dp))
                        } else {
                            Text(
                                paso.id.toString(),
                                color = Color.White,
                                fontWeight = FontWeight.Bold,
                                fontSize = 14.sp
                            )
                        }
                    }
                }
                
                Spacer(Modifier.width(12.dp))
                
                Column {
                    Text(
                        text = paso.titulo,
                        style = MaterialTheme.typography.titleSmall,
                        fontWeight = FontWeight.Bold,
                        color = if (isLocked) Color.Gray else Color.Black
                    )
                    Text(
                        text = "Actor: ${paso.actor}",
                        style = MaterialTheme.typography.labelSmall,
                        color = AzulPrimario
                    )
                }
                
                if (isLocked) {
                    Spacer(Modifier.weight(1f))
                    Icon(Icons.Default.Lock, null, tint = GrisBloqueado, modifier = Modifier.size(16.dp))
                }
            }

            if (!isLocked) {
                Spacer(Modifier.height(12.dp))
                
                Text(
                    text = "Acción:",
                    style = MaterialTheme.typography.labelMedium,
                    fontWeight = FontWeight.Bold
                )
                Text(
                    text = paso.accion,
                    style = MaterialTheme.typography.bodyMedium
                )

                Spacer(Modifier.height(8.dp))
                
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Default.Schedule, null, tint = AzulPrimario, modifier = Modifier.size(14.dp))
                    Spacer(Modifier.width(4.dp))
                    Text(
                        text = "Plazo: ${paso.plazoHorario}",
                        style = MaterialTheme.typography.bodySmall,
                        fontWeight = FontWeight.SemiBold,
                        color = AzulPrimario
                    )
                }

                paso.alerta?.let { alerta ->
                    Spacer(Modifier.height(12.dp))
                    Surface(
                        modifier = Modifier.fillMaxWidth(),
                        color = MaterialTheme.colorScheme.errorContainer,
                        shape = RoundedCornerShape(8.dp)
                    ) {
                        Row(
                            modifier = Modifier.padding(8.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(Icons.Default.Warning, null, tint = MaterialTheme.colorScheme.error, modifier = Modifier.size(18.dp))
                            Spacer(Modifier.width(8.dp))
                            Text(
                                text = alerta,
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onErrorContainer
                            )
                        }
                    }
                }

                if (isActive) {
                    Spacer(Modifier.height(16.dp))
                    Button(
                        onClick = onComplete,
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(10.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = AzulPrimario)
                    ) {
                        Text("Marcar como Completado / Siguiente")
                        Spacer(Modifier.width(8.dp))
                        Icon(Icons.AutoMirrored.Filled.ArrowForward, null, modifier = Modifier.size(16.dp))
                    }
                }
            }
        }
    }
}
